import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, IdentificationBadge, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ROLE_LABELS, PERMS, useAuth, canManage } from "@/contexts/AuthContext";
import GuidedTour from "@/components/GuidedTour";
import TourHelpButton from "@/components/TourHelpButton";
import { useGuidedTour } from "@/hooks/useGuidedTour";

const TOUR_STORAGE_KEY = "chkr_tour_usuarios_seen";

const TOUR_STEPS = [
  { target: "[data-tour='usr-nuevo']", title: "Crear una cuenta", content: "Da de alta un usuario con su email, contraseña, rol y — si aplica — el empleado al que se vincula (necesario para que pueda usar el Portal)." },
  { target: "[data-tour='usr-tabla']", title: "Cuentas y roles", content: "Consulta el rol de cada usuario y su estado. Da clic en el lápiz para editar rol, contraseña o desactivar la cuenta. Cada cambio queda en la bitácora de auditoría." },
];

const ROLES = ["super_admin", "admin_empresa", "rh", "supervisor", "jefe_area", "empleado", "auditor", "solo_lectura"];

export default function Usuarios() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const { active: tourActive, start: startTour, finish: finishTour } = useGuidedTour(TOUR_STORAGE_KEY);

  const load = async () => {
    const [u, e, p] = await Promise.all([api.get("/usuarios"), api.get("/empresas"), api.get("/empleados")]);
    setItems(u.data);
    setEmpresas(e.data);
    setEmpleados(p.data);
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditing(null);
    setForm({
      name: "", email: "", password: "", role: "empleado",
      // super_admin DEBE elegir empresa; el resto queda atado a la suya (el backend lo fuerza).
      empresa_id: isSuper ? "" : (user?.empresa_id || ""),
      empleado_id: "",
    });
    setOpen(true);
  };
  const startEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: "", role: u.role, empresa_id: u.empresa_id || "", empleado_id: u.empleado_id || "", activo: u.activo });
    setOpen(true);
  };

  // Borrado lógico: para corregir un alta equivocada. La cuenta se conserva en BD
  // pero desaparece del listado, no puede iniciar sesión y su correo queda libre
  // para volver a darlo de alta. Para suspender una cuenta en uso se usa el campo
  // "activo" del formulario de edición, no esto.
  const remove = async (u) => {
    if (!window.confirm(`Eliminar la cuenta de "${u.name}" (${u.email}). Úsalo sólo para corregir un alta equivocada: la cuenta deja de aparecer y no podrá iniciar sesión. ¿Continuar?`)) return;
    try {
      await api.delete(`/usuarios/${u.id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Nadie edita su propio rol desde aquí — ni siquiera super_admin (evita tanto una
  // escalada de privilegios como un autobloqueo). El backend ya lo exige; esto solo
  // evita ofrecer en la interfaz algo que de todos modos va a rechazar.
  const editandoPropiaCuenta = !!(editing && user && editing.id === user.id);
  // Un no-super tampoco puede asignar un rol igual o superior al suyo a NADIE — el
  // dropdown solo debe ofrecer roles que en verdad puede otorgar.
  const rolesAsignables = isSuper ? ROLES : ROLES.filter((r) => (PERMS[r] || 0) < (PERMS[user?.role] || 0));
  // La empresa es obligatoria para cuentas normales (a qué compañía pertenecen). Una
  // cuenta super_admin EXISTENTE es global por diseño y nunca tiene empresa — exigirla
  // ahí bloqueaba, entre otras cosas, que un super_admin editara su propia cuenta (ej.
  // para cambiar su contraseña) sin antes "asignarse" a una empresa que no le corresponde.
  // (Crear un super_admin nuevo sigue pidiendo empresa: eso lo exige el backend aparte,
  // no es parte de este arreglo.)
  const empresaEsObligatoria = isSuper && !(editing && form.role === "super_admin");

  const submit = async () => {
    if (empresaEsObligatoria && !form.empresa_id) {
      toast.error("Selecciona la empresa a la que pertenece la cuenta");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/usuarios/${editing.id}`, payload);
        toast.success("Usuario actualizado");
      } else {
        await api.post("/auth/register", form);
        toast.success("Usuario creado");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="usuarios-page">
      <PageHeader
        kicker="Acceso"
        title="Usuarios y Permisos"
        subtitle="Administra cuentas y roles. Cada acción queda en bitácora de auditoría."
        actions={
          <div className="flex gap-2 items-center">
            <Button onClick={startCreate} className="bg-brand hover:bg-brand-hover" data-testid="usr-new-btn" data-tour="usr-nuevo">
              <Plus size={16} weight="bold" className="mr-2" /> Nuevo usuario
            </Button>
            <TourHelpButton onClick={startTour} />
          </div>
        }
      />

      <GuidedTour steps={TOUR_STEPS} active={tourActive} onFinish={finishTour} />

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-tour="usr-tabla">
        <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Usuario</th>
              <th>Email</th>
              {isSuper && <th>Empresa</th>}
              <th>Rol</th>
              <th>Empleado vinculado</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} data-testid={`usr-row-${u.id}`}>
                <td>
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                      <IdentificationBadge size={18} weight="duotone" />
                    </div>
                    <div className="font-semibold">{u.name}{u.id === user?.id && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-bold">(tú)</span>}</div>
                  </div>
                </td>
                <td className="font-mono text-xs">{u.email}</td>
                {isSuper && (
                  <td className="text-xs text-slate-600">
                    {empresas.find((e) => e.id === u.empresa_id)?.razon_social
                      || (u.empresa_id ? "—" : <span className="text-amber-600">sin empresa</span>)}
                  </td>
                )}
                <td><span className="text-[10px] uppercase tracking-wider font-bold text-slate-700 bg-slate-100 px-2 py-1 rounded">{ROLE_LABELS[u.role] || u.role}</span></td>
                <td className="text-xs">{empleados.find((e) => e.id === u.empleado_id)?.nombre || "—"}</td>
                <td>
                  <span className={`text-xs px-2 py-0.5 rounded ${u.activo ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                    {u.activo ? "Activo" : "Inactivo"}
                  </span>
                </td>
                <td>
                  <Button variant="ghost" size="sm" onClick={() => startEdit(u)} data-testid={`usr-edit-${u.id}`}><PencilSimple size={16} /></Button>
                  {canManage(user) && u.id !== user?.id && (
                    <Button variant="ghost" size="sm" onClick={() => remove(u)} data-testid={`usr-del-${u.id}`} title="Eliminar (corregir alta)"><Trash size={16} className="text-red-600" /></Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="usr-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2 space-y-1"><Label className="label-uppercase">Nombre</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="usr-name-input" /></div>
            {/* autoComplete: sin esto Chrome llena el correo y la contraseña de quien está logueado (la cuenta nueva quedaría con su contraseña). */}
            <div className="sm:col-span-2 space-y-1"><Label className="label-uppercase">Email</Label><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} autoComplete="off" data-testid="usr-email-input" /></div>
            <div className="sm:col-span-2 space-y-1"><Label className="label-uppercase">{editing ? "Nueva contraseña (opcional)" : "Contraseña"}</Label><Input type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" data-testid="usr-password-input" /></div>
            {empresaEsObligatoria ? (
              <div className="sm:col-span-2 space-y-1">
                <Label className="label-uppercase">Empresa</Label>
                <Select
                  value={form.empresa_id || ""}
                  onValueChange={(v) => setForm({ ...form, empresa_id: v, empleado_id: null })}
                >
                  <SelectTrigger data-testid="usr-empresa-input"><SelectValue placeholder="Selecciona una empresa" /></SelectTrigger>
                  <SelectContent>
                    {empresas.map((e) => <SelectItem key={e.id} value={e.id}>{e.razon_social}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400">La cuenta solo verá y gestionará datos de esta empresa.</p>
              </div>
            ) : (
              isSuper && (
                <div className="sm:col-span-2">
                  <p className="text-xs text-slate-400">Una cuenta Super Admin es global — no pertenece a ninguna empresa en particular.</p>
                </div>
              )
            )}
            <div className="space-y-1">
              <Label className="label-uppercase">Rol</Label>
              <Select
                value={form.role || "empleado"}
                onValueChange={(v) => setForm({ ...form, role: v })}
                disabled={editandoPropiaCuenta}
              >
                <SelectTrigger data-testid="usr-role-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {/* Si el rol actual de la cuenta ya no es asignable por este actor (p.ej. es
                      igual o superior al suyo), se muestra igual como única opción de solo lectura
                      para que el Select no se quede sin valor visible. */}
                  {(rolesAsignables.includes(form.role) ? rolesAsignables : [form.role, ...rolesAsignables])
                    .map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}
                </SelectContent>
              </Select>
              {editandoPropiaCuenta && (
                <p className="text-xs text-slate-400">No puedes cambiar tu propio rol — pide a otro administrador que lo haga.</p>
              )}
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Empleado</Label>
              <Select value={form.empleado_id || "ninguno"} onValueChange={(v) => setForm({ ...form, empleado_id: v === "ninguno" ? null : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ninguno">— Sin vincular —</SelectItem>
                  {empleados
                    .filter((e) => !form.empresa_id || e.empresa_id === form.empresa_id)
                    .map((e) => <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-hover" data-testid="usr-save-btn">{busy ? "Guardando…" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
